import type { FC, KeyboardEvent, PointerEvent, ReactNode } from 'react'
import type { Drawing, Pt } from '@/lib/drawings/types'

import { useId, useMemo, useRef, useState } from 'react'
import { createRng } from '@/lib/cloud/rng'
import { getDrawingSet } from '@/lib/drawings/buildings'
import { DrawingSheet, layoutDrawing, SHEET_HEIGHT, SHEET_WIDTH } from '@/components/projects/drawing-sheet'

interface ScanVsDesignProps {
    projectId: string
    shape: string
    name: string
    deviationMm: number | null
    deviationNote: string
}

interface ScanPoint {
    x: number
    y: number
    mm: number
}

const BLUE = '#86B6F0'
const CREAM = '#ECE6D8'
const GREY = '#8A8478'

const pickView = (shape: string): Drawing => {
    const set = getDrawingSet(shape.startsWith('generic') ? 'generic' : shape)
    const views = [set.section, set.elevation, set.plan]

    return views.find(view => view.deviation) ?? set.section
}

/**
 * Samples the drawing's strokes as an as-built scan: small random deviations everywhere and a peak at
 * the building's largest recorded deviation, in screen coordinates of the shared sheet.
 */
const sampleScan = (drawing: Drawing, peak: number, seed: number): Array<ScanPoint> => {
    const { toScreen, scale } = layoutDrawing(drawing)
    const rng = createRng(seed)
    const hotspot = drawing.deviation ? toScreen(drawing.deviation) : null
    const radius = Math.max(36, scale * 3.2)
    const points: Array<ScanPoint> = []

    for (const stroke of drawing.strokes) {
        if (stroke.weight === 'hidden' || stroke.weight === 'thin') continue

        const screen = stroke.points.map(toScreen)

        if (stroke.closed) screen.push(screen[0])

        for (let i = 0; i < screen.length - 1; i++) {
            const [ax, ay] = screen[i]
            const [bx, by] = screen[i + 1]
            const length = Math.hypot(bx - ax, by - ay)
            const count = Math.max(1, Math.floor(length / 2.6))
            const nx = length > 0 ? -(by - ay) / length : 0
            const ny = length > 0 ? (bx - ax) / length : 0

            for (let k = 0; k <= count; k++) {
                const t = k / count
                const x = ax + (bx - ax) * t
                const y = ay + (by - ay) * t
                let deviation = 0.3 + rng() * 2.4

                if (rng() < 0.06) deviation += 1.5 + rng() * 2.5

                if (hotspot) {
                    const d = Math.hypot(x - hotspot[0], y - hotspot[1])

                    deviation = Math.max(deviation, peak * Math.exp(-(d * d) / (2 * radius * radius)))
                }

                const push = (deviation / 4) * (rng() < 0.5 ? -1 : 1)

                points.push({ x: x + nx * push + (rng() - 0.5) * 0.8, y: y + ny * push + (rng() - 0.5) * 0.8, mm: deviation })
            }
        }
    }

    return points
}

const squares = (points: Array<ScanPoint>): string => points.map(point => `M${point.x.toFixed(1)} ${point.y.toFixed(1)}h1.6v1.6h-1.6z`).join('')

/**
 * The design drawing on one side and the as-built scan on the other, split by a draggable handle.
 * Scan points are coloured by deviation: grey under 3 mm, cream between, blue over 8 mm.
 */
export const ScanVsDesign: FC<ScanVsDesignProps> = ({ projectId, shape, name, deviationMm, deviationNote }): ReactNode => {
    const drawing = useMemo(() => pickView(shape), [shape])
    const peak = deviationMm ?? 6
    const points = useMemo(() => sampleScan(drawing, peak, shape.length * 131 + Math.round(peak * 10)), [drawing, peak, shape])
    const buckets = useMemo(() => ({
        low: squares(points.filter(point => point.mm < 3)),
        mid: squares(points.filter(point => point.mm >= 3 && point.mm <= 8)),
        high: squares(points.filter(point => point.mm > 8))
    }), [points])
    const layout = useMemo(() => layoutDrawing(drawing), [drawing])
    const hotspot: Pt | null = drawing.deviation ? layout.toScreen(drawing.deviation) : null
    const [split, setSplit] = useState(50)
    const frameRef = useRef<HTMLDivElement>(null)
    const dragging = useRef(false)
    const clipId = useId().replace(/:/g, '')
    const splitX = (split / 100) * SHEET_WIDTH
    const labelLeft = hotspot ? hotspot[0] > SHEET_WIDTH * 0.7 : false
    const peakColour = peak > 8 ? BLUE : peak >= 3 ? CREAM : GREY
    const peakClass = peak > 8 ? 'text-signal' : peak >= 3 ? 'text-ink' : 'text-mute'

    const moveTo = (clientX: number): void => {
        const frame = frameRef.current

        if (!frame) return

        const rect = frame.getBoundingClientRect()
        const value = ((clientX - rect.left) / Math.max(1, rect.width)) * 100

        setSplit(Math.min(100, Math.max(0, value)))
    }

    const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        dragging.current = true
        event.currentTarget.setPointerCapture(event.pointerId)
        moveTo(event.clientX)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        if (dragging.current) moveTo(event.clientX)
    }

    const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
        dragging.current = false

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const steps: Record<string, number> = { ArrowLeft: -4, ArrowRight: 4, PageDown: -20, PageUp: 20 }

        if (event.key === 'Home') setSplit(0)
        else if (event.key === 'End') setSplit(100)
        else if (steps[event.key]) setSplit(value => Math.min(100, Math.max(0, value + steps[event.key])))
        else return

        event.preventDefault()
    }

    return (
        <div>
            <div
                ref={frameRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className='relative cursor-ew-resize touch-pan-y select-none border border-hairline'
            >
                <svg viewBox={`0 0 ${SHEET_WIDTH} ${SHEET_HEIGHT}`} className='block h-auto w-full' role='img' aria-label={`Design drawing of ${name} on the left and the as-built scan on the right, coloured by deviation from the design. The largest deviation is ${peak} mm.`}>
                    <defs>
                        <clipPath id={`${clipId}-design`}>
                            <rect x={0} y={0} width={splitX} height={SHEET_HEIGHT} />
                        </clipPath>

                        <clipPath id={`${clipId}-scan`}>
                            <rect x={splitX} y={0} width={SHEET_WIDTH - splitX} height={SHEET_HEIGHT} />
                        </clipPath>
                    </defs>

                    <g clipPath={`url(#${clipId}-design)`}>
                        <DrawingSheet drawing={drawing} animate={false} />
                    </g>

                    <g clipPath={`url(#${clipId}-scan)`}>
                        <path d={buckets.low} fill={GREY} fillOpacity={0.85} />
                        <path d={buckets.mid} fill={CREAM} fillOpacity={0.9} />
                        <path d={buckets.high} fill={BLUE} fillOpacity={0.95} />
                    </g>

                    {hotspot && (
                        <g>
                            <circle cx={hotspot[0]} cy={hotspot[1]} r={14} fill='none' stroke={peakColour} strokeWidth={1} />
                            <line
                                x1={hotspot[0] + (labelLeft ? -14 : 14)}
                                y1={hotspot[1] - 4}
                                x2={hotspot[0] + (labelLeft ? -60 : 60)}
                                y2={hotspot[1] - 46}
                                stroke={CREAM}
                                strokeOpacity={0.6}
                                strokeWidth={0.8}
                            />
                        </g>
                    )}

                    <line x1={splitX} y1={0} x2={splitX} y2={SHEET_HEIGHT} stroke={BLUE} strokeWidth={1} />
                    <text x={Math.max(12, splitX - 12)} y={22} textAnchor='end' fill='#8A8478' className='font-mono text-[10px] tracking-[0.12em]'>
                        DESIGN
                    </text>
                    <text x={Math.min(SHEET_WIDTH - 12, splitX + 12)} y={22} fill='#8A8478' className='font-mono text-[10px] tracking-[0.12em]'>
                        AS BUILT
                    </text>
                </svg>

                {hotspot && (
                    <div
                        className='pointer-events-none absolute border border-hairline bg-background px-2.5 py-1.5'
                        style={{
                            left: `${((hotspot[0] + (labelLeft ? -60 : 60)) / SHEET_WIDTH) * 100}%`,
                            top: `${((hotspot[1] - 46) / SHEET_HEIGHT) * 100}%`,
                            transform: labelLeft ? 'translate(-100%, -100%)' : 'translate(0, -100%)'
                        }}
                    >
                        <p className='hud whitespace-nowrap text-ink'>
                            <span className={peakClass}>
                                {peak} mm
                            </span>

                            <span className='text-mute'>
                                {' / '}
                            </span>

                            <span data-mdlfy-cms={`projects:${projectId}:deviation_note`}>
                                {deviationNote || 'Largest deviation'}
                            </span>
                        </p>
                    </div>
                )}

                <div
                    role='slider'
                    tabIndex={0}
                    aria-label='Split between design and scan'
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(split)}
                    aria-valuetext={`${Math.round(split)} percent design, ${100 - Math.round(split)} percent scan`}
                    onKeyDown={onKeyDown}
                    className='absolute top-1/2 grid size-9 -translate-x-1/2 -translate-y-1/2 place-items-center border border-signal bg-background text-signal'
                    style={{ left: `${split}%` }}
                >
                    <svg viewBox='0 0 16 16' className='size-4' aria-hidden='true'>
                        <path d='M6 4 L2 8 L6 12 M10 4 L14 8 L10 12' fill='none' stroke='currentColor' strokeWidth={1.2} />
                    </svg>
                </div>
            </div>

            <div className='mt-4 flex flex-wrap items-center gap-x-6 gap-y-2' aria-hidden='true'>
                <span className='hud flex items-center gap-2 text-mute'>
                    <span className='size-2 bg-mute' />
                    Under 3 mm
                </span>

                <span className='hud flex items-center gap-2 text-mute'>
                    <span className='size-2 bg-ink' />
                    3 to 8 mm
                </span>

                <span className='hud flex items-center gap-2 text-mute'>
                    <span className='size-2 bg-signal' />
                    Over 8 mm
                </span>

                <span className='hud ml-auto text-mute'>
                    Drag the handle or use the arrow keys
                </span>
            </div>
        </div>
    )
}
